import type { DateTime } from 'luxon';
import type { ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { RRule, type Weekday } from 'rrule';
import type { ParsedRRule } from '@/pages/Schedule/utils';
import { Flex, Select, type SelectProps } from '@/ui';
import { RecurrenceOptions } from './constants';
import { MonthRecurrenceSelect } from './MonthRecurrenceSelect';
import type { RecurrenceOption } from './types';
import { WeekdaySelect } from './WeekdaySelect';

const getSelectedOption = (
  parsedRRule: Pick<ParsedRRule, 'frequency' | 'interval'>
): RecurrenceOption => {
  const selected = RecurrenceOptions.find(({ rule }) => {
    return (
      rule.freq === parsedRRule.frequency &&
      rule.interval === parsedRRule.interval
    );
  });

  return selected ?? RecurrenceOptions[0];
};

type Props = Omit<SelectProps, 'onChange'> &
  Pick<ParsedRRule, 'frequency' | 'byWeekday' | 'interval'> & {
    rule: string | null;
    startDate: DateTime;
    onChange: (option: RecurrenceOption) => void;
    onWeekdayChange: (weekday: Weekday[]) => void;
    onMonthChange: (weekday: Weekday | null) => void;
  };

export const RecurrenceSelect = ({
  rule,
  frequency,
  interval,
  byWeekday,
  startDate,
  onChange,
  onWeekdayChange,
  onMonthChange,
  children,
  ...styleProps
}: Props) => {
  const { t } = useTranslation('common', { keyPrefix: 'recurrence_select' });
  const selected = getSelectedOption({ frequency, interval });

  const handleFrequencyChange: ChangeEventHandler<HTMLSelectElement> = (
    event
  ) => {
    const option = RecurrenceOptions.find(
      (option) => option.label === event.target.value
    );
    if (option) {
      onChange(option);
    }
  };

  return (
    <Flex direction="column" flex="1" gap="2">
      <Select
        aria-label={t('aria')}
        value={selected.label}
        onChange={handleFrequencyChange}
        {...styleProps}
      >
        {RecurrenceOptions.map(({ label }) => (
          <option key={label} value={label}>
            {label}
          </option>
        ))}
      </Select>

      {frequency === RRule.WEEKLY ? (
        <WeekdaySelect value={byWeekday ?? []} onChange={onWeekdayChange} />
      ) : frequency === RRule.MONTHLY ? (
        <MonthRecurrenceSelect
          rule={rule}
          {...styleProps}
          startDate={startDate}
          onChange={onMonthChange}
        />
      ) : null}

      {children}
    </Flex>
  );
};
